import React, { useContext, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaPlus, FaEdit, FaTrash, FaSave, FaTimes, FaSpinner, FaImage, FaUser } from 'react-icons/fa';
import { usePortfolio } from '../../context/PortfolioContext.jsx';
import { uploadAPI } from '../../utils/api.js';
import VisibilityToggle from './VisibilityToggle';

const emptyForm = { name: '', position: '', text: '', image: '', visible: true };

const TestimonialsEditor = () => {
  const { portfolioData, updatePortfolioData, loading } = usePortfolio();
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [imageUploading, setImageUploading] = useState(false);

  const testimonials = portfolioData?.testimonials || [];

  const handleChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleImageUpload = async (file) => {
    setError('');
    setMessage('');
    if (!file) return;
    setImageUploading(true);
    try {
      const response = await uploadAPI.uploadImage(file);
      if (response.success) {
        setForm(prev => ({ ...prev, image: response.data.url }));
        setMessage('Image uploaded!');
      } else {
        setError(response.error || 'Failed to upload image');
      }
    } catch (err) {
      setError('Error uploading image');
    } finally {
      setImageUploading(false);
    }
  };

  // Add this function for toggling visibility in the testimonials list and persist to backend
  const handleTestimonialChange = async (index, field, value) => {
    const updatedItems = [...testimonials];
    updatedItems[index] = {
      ...updatedItems[index],
      [field]: value,
    };
    // setTestimonials(updatedItems); // Uncomment if you have a testimonials state
    const updatedTestimonial = updatedItems[index];
    try {
      await updatePortfolioData('testimonials-update', updatedTestimonial, updatedTestimonial.id);
    } catch (error) {
      console.error('Failed to update testimonial visibility:', error);
    }
  };

  const handleEdit = t => {
    setForm({ name: t.name, position: t.position, text: t.text, image: t.image || '', visible: t.visible });
    setEditingId(t.id);
  };

  const handleCancel = () => {
    setForm(emptyForm);
    setEditingId(null);
    setError('');
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');
    setMessage('');
    if (!form.name || !form.text) {
      setError('Name and testimonial text are required.');
      return;
    }
    setIsSaving(true);
    try {
      const newItem = {
        ...form,
        id: editingId || form.id || Date.now(),
        visible: form.visible
      };
      if (editingId) {
        await updatePortfolioData('testimonials-update', newItem, editingId);
        setMessage('Testimonial updated!');
      } else {
        await updatePortfolioData('testimonials', newItem);
        setMessage('Testimonial added!');
      }
      handleCancel();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async id => {
    if (!window.confirm('Delete this testimonial?')) return;
    await updatePortfolioData('testimonials-delete', {}, id);
    if (editingId === id) handleCancel();
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gray-900 rounded-2xl p-8 shadow-lg max-w-4xl mx-auto relative"
    >
      <h3 className="text-2xl font-bold text-primary mb-8 text-center flex items-center justify-center gap-3">
        <FaUser className="text-primary" />
        Manage Testimonials
      </h3>

      {/* Add/Edit Form */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gray-800 rounded-xl p-6 mb-8 border border-gray-700"
      >
        <h4 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <FaPlus className="text-primary" />
          {editingId ? 'Edit Testimonial' : 'Add New Testimonial'}
        </h4>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Name *</label>
              <input 
                name="name" 
                value={form.name} 
                onChange={handleChange} 
                placeholder="Enter name" 
                className="w-full px-4 py-3 rounded-lg bg-gray-700 text-white border border-gray-600 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors" 
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Position (optional)</label>
              <input 
                name="position" 
                value={form.position} 
                onChange={handleChange} 
                placeholder="Enter position" 
                className="w-full px-4 py-3 rounded-lg bg-gray-700 text-white border border-gray-600 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors" 
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Testimonial Text *</label>
            <textarea 
              name="text" 
              value={form.text} 
              onChange={handleChange} 
              placeholder="Enter testimonial text" 
              rows="4"
              className="w-full px-4 py-3 rounded-lg bg-gray-700 text-white border border-gray-600 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors resize-vertical" 
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2 flex items-center gap-2">
              <FaImage className="text-primary" />
              Profile Image (optional)
            </label>
            {form.image && (
              <div className="mb-3 flex items-center gap-3">
                <img src={form.image} alt={form.name} className="h-16 w-16 rounded-full border-2 border-gray-600 object-cover" />
                <button 
                  type="button" 
                  onClick={() => setForm(prev => ({ ...prev, image: '' }))} 
                  className="text-red-400 hover:text-red-300 text-sm flex items-center gap-1 transition-colors"
                >
                  <FaTimes />
                  Remove
                </button>
              </div>
            )}
            <input 
              type="file" 
              accept="image/*" 
              className="block w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-primary/20 file:text-primary hover:file:bg-primary/30 transition-colors" 
              onChange={e => { const file = e.target.files[0]; if (file) handleImageUpload(file); }} 
            />
            {imageUploading && (
              <div className="mt-2 flex items-center gap-2 text-primary">
                <FaSpinner className="animate-spin" />
                <span className="text-sm">Uploading image...</span>
              </div>
            )}
          </div>
          
          <div className="flex items-center gap-3">
            <VisibilityToggle
              checked={form.visible}
              onChange={e => setForm({ ...form, visible: e.target.checked })}
              loading={false}
            />
            <span className="text-sm text-gray-300">Make this testimonial visible on the website</span>
          </div>
          
          {error && (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-red-400 text-sm bg-red-900/20 border border-red-800 rounded-lg p-3"
            >
              {error}
            </motion.div>
          )}
          
          {message && (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-green-400 text-sm bg-green-900/20 border border-green-800 rounded-lg p-3"
            >
              {message}
            </motion.div>
          )}
          
          <div className="flex gap-3 justify-end pt-4">
            {editingId && (
              <button 
                type="button" 
                onClick={handleCancel} 
                className="px-6 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors flex items-center gap-2"
              >
                <FaTimes />
                Cancel
              </button>
            )}
            <button 
              type="submit" 
              className="px-6 py-2 bg-primary text-white rounded-lg flex items-center gap-2 hover:bg-primary-dark disabled:opacity-50 transition-colors" 
              disabled={isSaving || loading}
            >
              {isSaving ? (
                <>
                  <FaSpinner className="animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <FaSave />
                  {editingId ? 'Update' : 'Add'} Testimonial
                </>
              )}
            </button>
          </div>
        </form>
      </motion.div>

      {/* Testimonials List */}
      <div className="space-y-4">
        <h4 className="text-lg font-semibold text-white mb-4">Existing Testimonials ({testimonials.length})</h4>
        
        {testimonials.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <FaUser className="text-4xl mx-auto mb-4 opacity-50" />
            <p>No testimonials added yet. Add your first testimonial above!</p>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            <AnimatePresence>
              {testimonials.map((t, index) => (
                <motion.div
                  key={t.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  className="bg-gray-800 rounded-xl p-6 border border-gray-700 hover:border-gray-600 transition-colors flex flex-col min-h-0"
                >
                  <div className="flex items-center gap-3 mb-4">
                    {t.image ? (
                      <img
                        src={t.image}
                        alt={t.name}
                        className="w-12 h-12 rounded-full object-cover border-2 border-gray-600"
                        onError={e => { e.target.onerror = null; e.target.src = '/default-profile.png'; }}
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-gray-700 flex items-center justify-center">
                        <FaUser className="text-gray-400" />
                      </div>
                    )}
                    <div className="flex-1">
                      <div className="font-semibold text-white">{t.name}</div>
                      {t.position && <div className="text-sm text-gray-400">{t.position}</div>}
                    </div>
                  </div>
                  
                  <div className="mb-4 flex-1 min-h-0">
                    <p className="text-gray-300 italic text-sm leading-relaxed line-clamp-3 hover:line-clamp-none transition-all duration-200 cursor-pointer break-words" title={t.text}>
                      "{t.text}"
                    </p>
                  </div>
                  
                  <div className="flex items-center justify-between mb-4">
                    <VisibilityToggle
                      checked={t.visible ?? true}
                      onChange={e => handleTestimonialChange(index, 'visible', e.target.checked)}
                      loading={false}
                    />
                    <span className={`text-xs px-2 py-1 rounded-full ${t.visible ? 'bg-green-900/30 text-green-400' : 'bg-gray-700 text-gray-400'}`}>
                      {t.visible ? 'Visible' : 'Hidden'}
                    </span>
                  </div>
                  
                  <div className="flex gap-2 mt-auto">
                    <button 
                      onClick={() => handleEdit(t)} 
                      className="flex-1 px-3 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors flex items-center justify-center gap-1 text-sm"
                    >
                      <FaEdit />
                      Edit
                    </button>
                    <button 
                      onClick={() => handleDelete(t.id)} 
                      className="flex-1 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center gap-1 text-sm"
                    >
                      <FaTrash />
                      Delete
                    </button>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default TestimonialsEditor; 